import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { get_users } from "../api";
import type { User } from "../api";
import type { SpecifyUserPermission } from "../api/types";
import { useLogin } from "../reducers";
import { buildVisibleUserOptions } from "./permissions";
import { usePeopleCompanies } from "./usePeopleCompanies";
import { getBrowserHash, splitHash, updateBrowserHash } from "./urlHash";
import type { HashParamKey } from "./urlHash";
import { DashboardPeopleSelectionContext } from "../components/dashboard/DashboardPeopleSelectionContext";
import { hasLocalActivityScope } from "../components/dashboard/activityScope";

const parseUserIds = (value: string): string[] | undefined =>
  value === "all"
    ? undefined
    : value
        .split(",")
        .map(part => part.trim())
        .filter(Boolean);

export const serializeUserFilterSelection = (
  userIds: string[],
  userOptions: Array<{ id: string }>,
): string =>
  userOptions.length > 0 &&
  userIds.length === userOptions.length &&
  userOptions.every(option => userIds.includes(option.id))
    ? "all"
    : userIds.join(",");

/**
 * URL people win on navigation; otherwise dashboard pages inherit the shared selection.
 * Keep the saved scope intact when a page has fewer permitted users. "all" stays dynamic
 * as visible users change; an empty value explicitly selects nobody.
 */
export const useUserFilterSelection = (
  hashKey: HashParamKey,
  permission: SpecifyUserPermission,
) => {
  const { t } = useTranslation();
  const location = useLocation();
  const localScope = hasLocalActivityScope(location);
  const { user } = useLogin();
  const { selection: rememberedSelection, remember } = useContext(DashboardPeopleSelectionContext);
  const [state, setState] = useState(() => ({
    key: location.key,
    hashKey,
    value: splitHash(getBrowserHash()).params.get(hashKey) ?? rememberedSelection,
  }));
  // Resolve before rendering consumers so navigation never fetches with the previous scope.
  let value = state.value;
  if (state.key !== location.key || state.hashKey !== hashKey) {
    value = splitHash(getBrowserHash()).params.get(hashKey) ?? rememberedSelection;
    setState({ key: location.key, hashKey, value });
  }
  const selection = useMemo(() => parseUserIds(value), [value]);
  useEffect(() => {
    if (state.key !== location.key || state.hashKey !== hashKey) return;
    updateBrowserHash({ [hashKey]: state.value });
    if (!localScope) remember(state.value);
  }, [state, location.key, hashKey, localScope, remember]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const companies = usePeopleCompanies(users);
  const userOptions = useMemo(
    () => buildVisibleUserOptions(permission, users, user, t, companies),
    [permission, users, user, t, companies],
  );
  const selectedUserIds = useMemo(
    () =>
      userOptions
        .filter(option => selection === undefined || selection.includes(option.id))
        .map(option => option.id),
    [selection, userOptions],
  );

  useEffect(() => {
    if (
      loadingUsers ||
      !usersLoaded ||
      selection === undefined ||
      (permission !== "All" && permission !== "AllCompany")
    )
      return;
    if (serializeUserFilterSelection(selection, userOptions) !== "all") return;
    setState(current => ({ ...current, value: "all" }));
  }, [loadingUsers, usersLoaded, permission, selection, userOptions]);

  const handleUserChange = useCallback(
    (userIds: string[]) => {
      const value = serializeUserFilterSelection(userIds, userOptions);
      setState(current => ({ ...current, value }));
      updateBrowserHash({ [hashKey]: value });
      if (!localScope) remember(value);
    },
    [hashKey, localScope, remember, userOptions],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    setUsersLoaded(false);
    get_users()
      .then(fetched => {
        if (!cancelled) {
          setUsers(fetched.items);
          setUsersLoaded(true);
          setLoadingUsers(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([user]);
          setLoadingUsers(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { selectedUserIds, userOptions, handleUserChange, loadingUsers };
};
